import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../state'
import * as display from '../../display'

const OTHER_DEFINITION_TYPE = 'Other'

@ngCore.Component({
  imports: [
    ngCommon.JsonPipe,
    display.value.Component,
    ds.input.text.Component,
    ds.input.select.single.Component,
    ds.input.code.Component,
  ],
  selector: 'atn-variable',
  standalone: true,
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component implements ngCore.OnDestroy {
  /** @see {@link state.admin.editingVariables.Feature} */
  private readonly _editingVariablesFeature = ngCore.inject(
    state.admin.editingVariables.Feature,
  )

  /**
   * Currently focused variable definition for editing
   */
  private readonly _focusedVariableDefinitionSg = ngCore.computed(
    (focused = this._editingVariablesFeature.selectors.focused.asSignal()) =>
      focused?.definition,
  )

  /**
   * Variable Definition id
   */
  public readonly definitionIdSg = ngCore.computed(
    (definition = this._focusedVariableDefinitionSg()) => definition?.id,
  )

  /**
   * Variable Definition name
   */
  public readonly definitionNameSg = ngCore.computed(
    (definition = this._focusedVariableDefinitionSg()) => definition?.name,
  )

  /**
   * Variable Definition type
   */
  public readonly definitionTypeSg = ngCore.computed(
    (definition = this._focusedVariableDefinitionSg()) => definition?.type,
  )

  /**
   * Variable Definition default value
   */
  public readonly definitionDefaultValueSg = ngCore.computed(
    (definition = this._focusedVariableDefinitionSg()) =>
      definition?.defaultValue,
  )

  /**
   * Variable Definition description
   */
  public readonly definitionDescriptionSg = ngCore.computed(
    (definition = this._focusedVariableDefinitionSg()) =>
      definition?.description,
  )

  /**
   * List of types that can be selected as intrinsic type
   */
  public readonly definitionTypeSelectItems: ds.input.select.models.Item[] = [
    ...ents.core.common.value.types.Intrinsic.ALL,
    OTHER_DEFINITION_TYPE,
  ].map(type => ({
    label: type,
    value: type,
  }))

  /**
   * Selected variable definition type
   */
  public readonly selectedDefinitionTypeSg = ngCore.computed(
    (
      type = this.definitionTypeSg(),
      isOtherTypeSelected = this.isOtherTypeSelectedSg(),
    ) => {
      if (ents.core.common.value.types.Intrinsic.isIntrinsic(type)) {
        return type
      }
      return isOtherTypeSelected ? OTHER_DEFINITION_TYPE : undefined
    },
  )

  /**
   * Returns true if selected type is not Intrinsic
   */
  public readonly isOtherTypeSelectedSg = ngCore.signal<boolean>(false)

  /**
   * Validates and transforms non-intrinsic type values for code editor
   * compatibility. When the selected type is not an intrinsic type, this signal
   * checks whether the type value requires transformation to ensure it can be
   * properly processed by the code editor.
   */
  public readonly otherTypeSg = ngCore.computed(
    (type = this.definitionTypeSg()) => {
      if (ents.core.dms.data.Value.Type.Enum.Inline.isInline(type)) {
        return {
          entries: type.inlineEnum.entries,
          isMultiselect: type.inlineEnum.isMultiselect,
        }
      }
      return type
    },
  )

  constructor() {
    // Sync selected type value of single-select component with variable
    // definition type. Needed when variable definition is being edited so
    // initial value is set properly
    ngCore.effect(() => {
      const definitionType = this.definitionTypeSg()
      if (
        definitionType &&
        !ents.core.common.value.types.Intrinsic.isIntrinsic(definitionType) &&
        !this.isOtherTypeSelectedSg()
      ) {
        this.isOtherTypeSelectedSg.set(true)
      }
    })
  }

  /**
   * Cleanup Other type selection. When component initialize again, we don't
   * want to have this option preselected
   */
  ngOnDestroy(): void {
    this.isOtherTypeSelectedSg.set(false)
  }

  /**
   * Update variable definition type when selected type is not intrinsic
   */
  public updateOtherType(type: unknown): void {
    this.updateDefinition(
      'type',
      ents.core.dms.data.Value.Type.Enum.Inline.AsJson.isAsJson(type)
        ? {
            inlineEnum: new ents.core.common.value.types.Enum(type.inlineEnum),
          }
        : (type as ents.core.dms.data.Value.Type),
    )
  }

  /**
   * __NOTE__: type param's type is set to unknown, since our ds-select
   * component emits unknown value, but that shouldn't be a problem since we
   * have if checks
   */
  public updateDefinitionType(type: unknown): void {
    if (ents.core.common.value.types.Intrinsic.isIntrinsic(type)) {
      this.updateDefinition('type', type)
    } else {
      this.updateDefinition('type', undefined)
    }

    this.isOtherTypeSelectedSg.set(type === OTHER_DEFINITION_TYPE)
    this.updateDefinition('defaultValue', undefined)
  }

  public updateDefinition<
    K extends keyof state.variableOverrides.models.Definition,
  >(
    prop: K,
    value: state.variableOverrides.models.Definition[K] | undefined,
  ): void {
    this._editingVariablesFeature.actions.updateFocused
      .from('Variable.Input.Changed')
      .dispatch(definition => ({ ...definition, [prop]: value }))
  }
}
